import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { FullStory } from '@fullstory/browser';

import { useGetVideoResources } from 'api/videoResources';
import { Button } from 'shared/components/Button/Button';
import { DownloadIcon } from 'shared/components/icons/DownloadIcon';
import useOnboardingStep from 'hooks/useOnboardingStep';
import { OnboardingStep } from 'modules/types/util';
import useUser from 'hooks/useUser';
import { useDownloadContract, useReviewedAgreement } from 'api/agreement';
import { toast } from 'shared/components/Toast/Toast';
import { Text } from 'shared/components/Text/Text';
import { VideoPlayer } from 'modules/support/components/VideoCard/VideoPlayer';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import {
  ActionContainer,
  BodyText,
  BodyTextContainer,
  ReviewAgreementContainer,
  TextDivider,
  Title,
  VideoPlayerWrapper,
} from './ReviewAgreement.styles';
import { ProductName, VideoResourceType } from '../../../../api/types';

const REVIEW_AGREEMENT_SCREEN_TAG = 'screen:review_agreement';

export function ReviewAgreement() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { selectedAgreement, refetch: refetchUser } = useUser();
  const { goToStep } = useOnboardingStep();

  const { data: videoResourcesData, isLoading: isLoadingVideos } = useGetVideoResources();
  const { mutate: reviewedAgreement } = useReviewedAgreement();

  const { mutate: download, isLoading } = useDownloadContract({
    onSuccess: (data) => {
      const { presigned_url: presignedUrl } = data;
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = 'signed_everbright_contract.pdf';
      link.click();
      link.remove();
    },
    onError: () => {
      toast({
        type: 'error',
        title: t('toast.error'),
        message: t('projectQualification.somethingWentWrong'),
        theme,
      });
    },
  });

  const handleReviewedAgreement = async () => {
    if (selectedAgreement && !selectedAgreement.reviewed_by_user) {
      await reviewedAgreement({ agreement_id: selectedAgreement?.id! });
      refetchUser();
    }
  };

  const handleClickPlay = () => {
    FullStory('trackEvent', {
      name: 'Onboarding - Clicked Play Contract Video',
      properties: {
        page: 'Status',
        onboardingStep: 'Review Agreement',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });
  };

  const video: VideoResourceType | null = useMemo(() => {
    const isShowVideo =
      selectedAgreement &&
      [ProductName.PPA, ProductName.PPA_PLUS, ProductName.SHIFT_PPA].includes(
        selectedAgreement.product_name as ProductName,
      );

    if (!isShowVideo || !videoResourcesData) return null;

    const agreementTagsSet = new Set(selectedAgreement?.tags.map((tag) => tag.toLowerCase()));
    const reviewAgreementVideo = videoResourcesData?.find((videoItem) => {
      const videoTagSet = new Set(videoItem.tags.map((tagItem) => tagItem.tag.toLowerCase()));
      return (
        !videoItem.hidden &&
        videoTagSet.has(REVIEW_AGREEMENT_SCREEN_TAG) &&
        videoTagSet.intersection(agreementTagsSet).size > 0
      );
    });

    return reviewAgreementVideo || null;
  }, [selectedAgreement, videoResourcesData]);

  const bodyList: string[] = t(
    video ? 'onboarding.reviewAgreement.haveVideoBody' : 'onboarding.reviewAgreement.noVideoBody',
    {
      returnObjects: true,
    },
  ) as string[];

  const handleClickDownload = () => {
    if (selectedAgreement && selectedAgreement?.contract_resource_id) {
      download(selectedAgreement.id);
      handleReviewedAgreement();
    } else {
      toast({
        type: 'error',
        title: t('toast.error'),
        message: t('projectQualification.somethingWentWrong'),
        theme,
      });
    }

    FullStory('trackEvent', {
      name: `Onboarding - Clicked Download On Review Your Agreement${video ? '' : ' (version without video)'}`,
      properties: {
        page: 'Status',
        onboardingStep: 'Review Agreement',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });
  };

  const handleClickNext = () => {
    handleReviewedAgreement();

    FullStory('trackEvent', {
      name: `Onboarding - Clicked Next On Review Your Agreement${video ? '' : ' (version without video)'}`,
      properties: {
        page: 'Status',
        onboardingStep: 'Review Agreement',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });
    goToStep(OnboardingStep.TPV);
  };

  return (
    <ReviewAgreementContainer>
      <Title as="h1">{t('onboarding.reviewAgreement.title')}</Title>

      <BodyTextContainer>
        {Array.isArray(bodyList) &&
          bodyList?.map((item: string) => (
            <BodyText as="p" key={item}>
              <Trans components={{ strong: <b /> }}>{item}</Trans>
            </BodyText>
          ))}
      </BodyTextContainer>

      {isLoadingVideos && (
        <ActionContainer>
          <LoadingSpinner />
        </ActionContainer>
      )}

      {!isLoadingVideos && video && (
        <>
          <VideoPlayerWrapper>
            <VideoPlayer
              url={video.url}
              onPlay={handleClickPlay}
              onPlaying={handleReviewedAgreement}
              width={474}
            />
          </VideoPlayerWrapper>

          <ActionContainer>
            <Button
              label={t('onboarding.reviewAgreement.button')}
              onClick={handleClickNext}
              isFullWidth
            />
            <Button
              styleVariant="tertiary"
              label={t('onboarding.reviewAgreement.downloadContract')}
              Icon={DownloadIcon}
              iconRight={false}
              isLoading={isLoading}
              onClick={handleClickDownload}
            />
          </ActionContainer>
        </>
      )}

      {!isLoadingVideos && !video && (
        <>
          <ActionContainer>
            <Button
              styleVariant="tertiary"
              label={t('onboarding.reviewAgreement.downloadContract')}
              Icon={DownloadIcon}
              iconRight={false}
              isLoading={isLoading}
              onClick={handleClickDownload}
              margin="12px 0px 0px 0px"
            />
          </ActionContainer>

          <TextDivider>
            <Text as="h3">{t('onboarding.reviewAgreement.whatNextTitle')}</Text>
          </TextDivider>

          <BodyTextContainer>
            <BodyText as="p">
              <Trans>{t('onboarding.reviewAgreement.whatNextBody')}</Trans>
            </BodyText>
          </BodyTextContainer>

          <ActionContainer>
            <Button
              label={t('onboarding.reviewAgreement.button')}
              onClick={handleClickNext}
              isFullWidth
              margin="24px 0px 0px 0px"
            />
          </ActionContainer>
        </>
      )}
    </ReviewAgreementContainer>
  );
}

export default ReviewAgreement;
